/**
 * Admin → Feature Flags
 *
 * The Discovery-owned feature-flag control plane (Phase A from
 * docs/architecture/13-CORE-DISCOVERY-INTEGRATION-MAP.md §5.2's
 * FeatureFlagSource adapter). Tenants paired with Core will see
 * the four `platform.*` flags get Core-pushed values in Phase B
 * (with `source='core_override'`); pure-Discovery flags stay local
 * and are managed here.
 *
 * Layout: searchable list grouped by scope (tenant override / global /
 * catalog default). Toggle + variant + rollout per row. Inline diff
 * vs current resolved value shown after save.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Flag as FlagIcon,
  Globe,
  Loader2,
  RefreshCw,
  Save,
  Trash2,
  XCircle,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  deleteTenantFlag,
  listFlags,
  SCOPE_META,
  upsertTenantFlag,
  upsertGlobalFlag,
  type Flag,
  type FlagScope,
} from '@/lib/feature-flags-api';
import { Globe2 } from 'lucide-react';

export function FeatureFlags(): JSX.Element {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<FlagScope | 'all'>('all');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFlags(await listFlags());
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return flags.filter((f) => {
      if (scopeFilter !== 'all' && f.scope !== scopeFilter) return false;
      if (!q) return true;
      return (
        f.key.toLowerCase().includes(q) ||
        (f.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [flags, search, scopeFilter]);

  const counts = useMemo(() => {
    const c: Record<FlagScope, number> = { tenant: 0, global: 0, default: 0 };
    for (const f of flags) c[f.scope]++;
    return c;
  }, [flags]);

  return (
    <PageContainer>
      <PageHeader
        title="Feature Flags"
        subtitle="Discovery-owned feature-flag control plane. Tenant overrides win over global defaults; catalog defaults apply when neither exists. Per CLAUDE.md §14 - independence rule - these flags work standalone; Core-overridden values arrive in Phase B."
      />

      <div className="grid grid-cols-4 gap-4 mb-6">
        <Kpi icon={<FlagIcon className="h-4 w-4" />} label="Total" value={String(flags.length)} />
        <Kpi icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />} label="Tenant overrides" value={String(counts.tenant)} />
        <Kpi icon={<Globe className="h-4 w-4 text-amber-500" />} label="Global defaults" value={String(counts.global)} />
        <Kpi icon={<FlagIcon className="h-4 w-4 text-muted-foreground" />} label="Catalog defaults" value={String(counts.default)} />
      </div>

      <GlassCard>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Flags</CardTitle>
              <CardDescription>{filtered.length} of {flags.length} shown</CardDescription>
            </div>
            <Button size="sm" variant="ghost" onClick={refresh} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        <div className="p-5 pt-0 space-y-3">
          {error && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-500">
              <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />
              {error}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by key or description…"
              className="flex-1 min-w-[200px] rounded-md border border-border bg-background/40 p-2 text-xs"
            />
            <select
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value as FlagScope | 'all')}
              className="rounded-md border border-border bg-background/40 p-2 text-xs"
            >
              <option value="all">All scopes</option>
              <option value="tenant">Tenant override</option>
              <option value="global">Global default</option>
              <option value="default">Catalog default</option>
            </select>
          </div>

          {filtered.length === 0 && !loading && (
            <div className="text-xs text-muted-foreground italic p-3">No flags match your filters.</div>
          )}

          <div className="space-y-2">
            {filtered.map((f) => (
              <FlagRow key={f.key} f={f} onChanged={refresh} />
            ))}
          </div>
        </div>
      </GlassCard>
    </PageContainer>
  );
}

function Kpi(props: { icon: React.ReactNode; label: string; value: string }): JSX.Element {
  return (
    <GlassCard>
      <div className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">{props.icon}<span>{props.label}</span></div>
        <div className="mt-2 text-2xl font-semibold">{props.value}</div>
      </div>
    </GlassCard>
  );
}

/**
 * The variant a flag takes when switched on: the one it has if that is not
 * 'off', else the first catalogue variant that is not 'off' (for
 * dspm.undeclared_personal_data, declared_units).
 */
export function variantWhenOn(f: Pick<Flag, 'variants'>, current: string): string {
  if (current !== 'off') return current;
  return (f.variants ?? []).find((v) => v !== 'off') ?? current;
}

export function FlagRow({ f, onChanged }: { f: Flag; onChanged: () => Promise<void> }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [enabled, setEnabled] = useState(f.enabled);
  const [variant, setVariant] = useState(f.variant);
  const [rollout, setRollout] = useState(f.rolloutPercentage);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const scopeMeta = SCOPE_META[f.scope];

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const updated = await upsertTenantFlag(f.key, {
        enabled,
        variant,
        payload: f.payload,
        rolloutPercentage: rollout,
      });
      setSavedAt(updated.updatedAt);
      await onChanged();
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setSaving(false);
    }
  }

  // Wave 5 Group-A (2026-05-27) - super-admin only: set the GLOBAL
  // default for this flag (applies to every tenant unless they have a
  // tenant-override row). Requires platform-admin role; non-super-admin
  // calls will 403 - surface that as a button error.
  async function saveGlobal(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const updated = await upsertGlobalFlag(f.key, {
        enabled,
        variant,
        payload: f.payload,
        rolloutPercentage: rollout,
      });
      setSavedAt(updated.updatedAt);
      await onChanged();
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setSaving(false);
    }
  }

  async function removeOverride(): Promise<void> {
    if (!confirm(`Remove tenant override for "${f.key}"? It will fall back to the global default.`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteTenantFlag(f.key);
      await onChanged();
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-background/40">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-start justify-between gap-3 p-4 text-left hover:bg-background/60 transition rounded-lg"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            <code className="text-xs font-semibold">{f.key}</code>
            <Badge variant={scopeMeta.tone} className="text-[10px]">{scopeMeta.label}</Badge>
            {f.enabled ? (
              <Badge variant="success" className="text-[10px]">
                <CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />ON
              </Badge>
            ) : (
              <Badge variant="glass" className="text-[10px]">OFF</Badge>
            )}
            <span className="text-[10px] text-muted-foreground">variant: {f.variant}</span>
            {f.rolloutPercentage > 0 && (
              <span className="text-[10px] text-muted-foreground">rollout: {f.rolloutPercentage}%</span>
            )}
          </div>
          {f.description && (
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{f.description}</p>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border p-4 space-y-3 bg-background/20">
          <p className="text-[10px] text-muted-foreground italic">{scopeMeta.description}</p>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground/80">Enabled</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  aria-label={`Enable ${f.key}`}
                  checked={enabled}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setEnabled(on);
                    // The store refuses enabled with 'off' and disabled with
                    // anything else, and the free text kept 'off': a DPO who
                    // ticked Enabled saved it off again (review of #450).
                    if (f.variants && f.variants.length > 0) setVariant(on ? variantWhenOn(f, variant) : 'off');
                  }}
                />
                <span className="text-xs">{enabled ? 'ON' : 'OFF'}</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground/80">Variant</label>
              {f.variants && f.variants.length > 0 ? (
                <select
                  aria-label={`Variant of ${f.key}`}
                  value={variant}
                  onChange={(e) => {
                    setVariant(e.target.value);
                    setEnabled(e.target.value !== 'off');
                  }}
                  className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
                >
                  {f.variants.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
                />
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-foreground/80">Rollout %</label>
              <input
                type="number"
                value={rollout}
                onChange={(e) => setRollout(Math.max(0, Math.min(100, parseInt(e.target.value || '0', 10))))}
                min={0}
                max={100}
                className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
              />
            </div>
          </div>

          {Object.keys(f.payload).length > 0 && (
            <div>
              <label className="text-xs font-medium text-foreground/80">Payload (read-only)</label>
              <pre className="mt-1 rounded-md bg-background/60 p-2 text-[10px] font-mono overflow-auto">
                {JSON.stringify(f.payload, null, 2)}
              </pre>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-500">
              <XCircle className="-mt-0.5 mr-1 inline h-3 w-3" />
              {error}
            </p>
          )}
          {savedAt && !error && (
            <p className="text-xs text-emerald-500">
              <CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />
              Saved at {new Date(savedAt).toLocaleString()}
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Button data-action="disc.adm_flags.a01" size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Save className="-mt-0.5 mr-1 inline h-3 w-3" />}
              Save as tenant override
            </Button>
            {/* Wave 5 Group-A - super-admin only; backend 403s
                non-platform-admin actors. Button title spells it out. */}
            <Button
              data-action="disc.adm_flags.a02"
              size="sm"
              variant="glass"
              onClick={saveGlobal}
              disabled={saving}
              title="Apply to every tenant (platform-admin role required; non-super-admin will 403)"
            >
              <Globe2 className="-mt-0.5 mr-1 inline h-3 w-3" />
              Save as global default
            </Button>
            {f.scope === 'tenant' && (
              <Button
                data-action="disc.adm_flags.a03"
                size="sm"
                variant="ghost"
                onClick={removeOverride}
                disabled={saving}
              >
                <Trash2 className="-mt-0.5 mr-1 inline h-3 w-3 text-red-500" />
                Remove override
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
